<template>
  <v-form
    ref="form"
    @keyup.enter="submit"
    @submit.prevent
  >
    <validation-observer ref="observer">
      <!-- Registration Form -->
      <v-card-title class="text-h5">
        {{ $t('title.verify_your_account') }}
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-5 pb-0">
        <p class="text-subtitle-1 text-center">
          {{ $t('message.enter_activation_code') }}
          <span class="font-weight-bold">{{ username }}</span>
        </p>
        <validation-provider
          v-slot="{ errors }"
          rules="required|min:4|max:4"
        >
          <v-otp-input
            ref="activationCode"
            v-model="activationCode"
            class="pb-0 mx-16"
            length="4"
            name="activationCode"
            :label="$t('label.activation_code')"
            :error-messages="errors"
            @finish="submit"
            @input="input"
          />
        </validation-provider>

        <v-btn
          class="mb-5"
          color="success"
          block
          large
          :disabled="activationCode && activationCode.length !== 4"
          @click="submit"
        >
          {{ $t('button.verify') }}
        </v-btn>
      </v-card-text>
      <v-card-actions :class="['flex-wrap', {'justify-center': $vuetify.breakpoint.xsOnly}]">
        <v-btn
          color="primary"
          large
          text
          @click="resend"
        >
          <v-icon class="mr-2">
            mdi-email
          </v-icon>
          {{ $t('button.resend_activation_email') }}
        </v-btn>
        <v-spacer v-if="$vuetify.breakpoint.smAndUp" />
        <v-btn
          color="error"
          text
          @click="logout"
        >
          {{ $t('button.close') }}
        </v-btn>
      </v-card-actions>
    </validation-observer>
  </v-form>
</template>

<script lang="ts">
import Vue from 'vue';
import { ValidationObserver } from 'vee-validate';
import { performAction } from '@/services/vue-utils';
import toast from '@/plugins/toast-plugin/toast';

export default Vue.extend({
  name: 'VerifyAccountStep',
  data() {
    return {
      activationCode: '',
    };
  },
  computed: {
    username(): string {
      return this.$tstore.getters.username ?? 'N/A';
    },
  },
  watch: { },
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.focusOtp();
      }, 300);
    });
  },
  methods: {
    focusOtp() {
      (this.$refs.activationCode as any)?.focus();
    },
    input() {
      this.activationCode = this.activationCode?.toUpperCase();
    },
    reset() {
      this.activationCode = '';
      (this.$refs.form as HTMLFormElement).reset();
      (this.$refs.observer as InstanceType<typeof ValidationObserver>).reset();
      this.focusOtp();
    },
    async logout() {
      this.close();
      try {
        await this.$tstore.dispatch('logout');
      } catch (e) {
        console.log('Logout error: ', e);
      }
      this.$vuetify.theme.dark = false;
    },
    async close() {
      this.reset();
      this.$emit('close');
    },
    async submit() {
      const isValid = await (this.$refs.observer as InstanceType<typeof ValidationObserver>).validate();
      if (!isValid) {
        toast.error(this.$i18n.tc('error.validation_failed'));
        return;
      }

      const ok = await performAction(
        this.$i18n.tc('message.registration_successful', undefined, [this.username]),
        this.$i18n.tc('error.registration_failed'),
        async () => {
          await this.$tstore.dispatch('register', { username: this.username, code: this.activationCode.toUpperCase() });
          await this.$tstore.dispatch('update');
        },
      );

      if (!ok) {
        this.reset();
      }
    },
    async resend() {
      const ok = await performAction(
        this.$i18n.tc('message.registration_code_sent_to_x_please_enter_code', undefined, [this.username]),
        this.$i18n.tc('error.registration_failed'),
        async () => this.$tstore.dispatch('register', { username: this.username }),
      );
    },
  },
});
</script>
